<template>
  <div class="mb-4 bg-lightblack" style="border-radius: 25px">
    <div ref="chatContainer" :style="styleMessages" class="pt-4" style="overflow-y: scroll; overflow-x: hidden">
      <state-loading v-if="isLoading" />

      <div v-if="currentDiscussionId">Discussion</div>

      <message-welcome v-if="!messages.length" />

      <data-message
        v-for="message in messages"
        :key="message.id"
        :id="message.id"
        :text="message.content"
        :url="message.url"
        :image-id="message.imageId"
        :audio-id="message.audioId"
        :audio-url="message.audioUrl"
        :sent-by="message.role"
        :is-discussion="message.isDiscussion"
        :picky-picture-url="DEFAULT_PICTURE"
        @reply="showReplyMessage"
        @remove="showRemoveMessage"
        @go-to-discussion="goToDiscussion"
      />
    </div>

    <div class="pl-4 pr-4 pt-4 pb-1 w-100" style="bottom: 0; min-height: 100px">
      <actions-options v-if="lastMessage?.options" @select="triggerSendMessage" :options="lastMessage.options" />

      <actions-reply-to v-if="replyToId" :to-user="replyToUser" :to-text="replyToText" />
      <actions-reply @success="triggerSendMessage" :is-sending="isSending" :is-disabled="isReplyDisabled" />

      <state-sending :is-sending="isSending" />
    </div>

    <modal-remove
      type="message"
      :name="`from ${replyToUser}`"
      :is-showing-modal.sync="isShowingModalRemove"
      :is-sending="isSending"
      @confirm="triggerRemove"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { FETCH, REMOVE, LISTEN, RESPOND } from "@/store/actions";

import { DEFAULT_PICTURE, LIMIT_PROMPT_MESSAGES } from "@/constants/ai";

import { textExcerpt } from "@/helpers/utils";

import StateLoading from "./State/Loading";
import StateSending from "./State/Sending";
import ActionsOptions from "./Actions/Options";
import ActionsReply from "./Actions/Reply";
import ActionsReplyTo from "./Actions/ReplyTo";
import DataMessage from "./Data/Message";
import ModalRemove from "./Modals/Remove";
import MessageWelcome from "./Welcome";

export default {
  name: "Chatbot",
  components: {
    StateLoading,
    StateSending,
    ActionsOptions,
    ActionsReply,
    ActionsReplyTo,
    DataMessage,
    ModalRemove,
    MessageWelcome,
  },
  computed: {
    ...mapGetters("aiChat", ["itemsSorted"]),
    messages() {
      return this.itemsSorted();
    },

    lastKey() {
      const { messages } = this;
      return messages[0]?.id;
    },
    lastMessage() {
      const { messages } = this;
      return messages[messages.length - 1];
    },

    styleMessages() {
      return `height: 80vh`;
    },

    isReplyDisabled() {
      const { lastMessage } = this;

      if (!lastMessage) return false;
      else if (lastMessage && lastMessage.isReplyDisabled) return true;
      return false;
    },
  },
  data: () => ({
    currentDiscussionId: null,

    replyToId: null,
    replyToText: null,
    replyToUser: null,

    DEFAULT_PICTURE,

    errorMessage: null,

    isShowingModalRemove: false,

    hasReachedTheEnd: false,

    isLoading: true,
    isSending: false,
    isShowing: false,
  }),
  mounted() {
    const { currentDiscussionId: discussionId } = this;

    this.triggerFetch({ discussionId });
    this.listen({ discussionId });
  },
  watch: {
    currentDiscussionId() {
      const { currentDiscussionId: discussionId } = this;

      this.triggerFetch({ discussionId });
      this.listen({ discussionId });
    },

    isShowing() {
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
  },
  methods: {
    ...mapActions("aiChat", { fetch: FETCH, remove: REMOVE, listen: LISTEN, respond: RESPOND }),
    async triggerFetch({ discussionId }) {
      this.isLoading = true;

      const { lastKey } = this;
      const { projectId } = this.$route.params;
      const { data } = await this.fetch({ projectId, discussionId, lastKey });

      if (!data) this.hasReachedTheEnd = true;

      this.isLoading = false;

      this.$nextTick(() => {
        this.scrollToTop();
      });
    },
    async triggerSendMessage({ content = null, ...others }) {
      console.log("others", { ...others });
      this.isSending = true;

      const { projectId } = this.$route.params;
      const { currentDiscussionId: discussionId } = this;

      try {
        const messages = this.sanitizeRequest();
        await this.respond({ projectId, discussionId, content, messages, ...others });
      } catch (error) {
        console.log("ERR", error);
        this.errorMessage = true;
      }

      this.hideReplyMessage();
      this.isSending = false;

      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    async triggerRemove() {
      this.isSending = true;

      const { replyToId: id } = this;
      const { projectId } = this.$route.params;
      await this.remove({ projectId, id });

      this.hideRemoveMessage();
      this.isSending = false;
    },

    sanitizeRequest() {
      const { messages, replyToId } = this;

      const sanitizeMessages = [];
      let sourceMessages = [...messages];
      console.log("messages", [...messages]);
      console.log("replyToId", replyToId);

      /*  if (replyToId) {
        const replyTo = sourceMessages.find(({ id }) => id === replyToId);
        console.log("replyTo", replyTo);
        const replyToMessages = [];
        replyToMessages.push(replyTo);
        replyToMessages.push(sourceMessages[sourceMessages.length - 1]);

        replyToMessages.forEach(({ id, role, content }) => {
          sanitizeMessages.push({ id, role, content });
        });
        console.log("sanitizeMessages", [...sanitizeMessages]);
        return sanitizeMessages;
      } */

      let i = 0;

      sourceMessages.reverse().forEach(({ role, content }) => {
        if (i < LIMIT_PROMPT_MESSAGES && role && content) {
          const newRole = role === "avatar" ? "assistant" : "user";
          sanitizeMessages.unshift({ role: newRole, content });
          i++;
        }
      });

      console.log("sanitizeMessages", sanitizeMessages);

      return sanitizeMessages;
    },

    showReplyMessage({ id, text, sentBy }) {
      this.replyToId = id;
      this.replyToText = textExcerpt(text, 200);
      this.replyToUser = sentBy === "user" ? "Myself" : "J.L. AI";
      // sentBy === user
      // sentBy === avatar
    },
    hideReplyMessage() {
      this.replyToId = null;
      this.replyToText = null;
      this.replyToUser = null;
    },
    showRemoveMessage({ id, sentBy }) {
      this.replyToId = id;
      this.replyToUser = sentBy === "user" ? "Myself" : "J.L. AI";

      this.isShowingModalRemove = true;
    },
    hideRemoveMessage() {
      this.replyToId = null;
      this.replyToUser = null;

      this.isShowingModalRemove = false;
    },

    scrollToTop() {
      this.$refs.chatContainer.scrollTop = 0;
    },
    scrollToBottom() {
      this.$refs.chatContainer.scrollTop = this.$refs.chatContainer.scrollHeight;
    },

    goToDiscussion(discussionId) {
      this.currentDiscussionId = discussionId;
    },
  },
};
</script>
